import { apiClient, getErrorMessage } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import {
  API_EDI_FIELD_MAPPING_RESOLVE,
  API_EDI_FIELD_MAPPING_LIST_ENTRIES,
  API_EDI_FIELD_MAPPING_SAVE,
} from '../../constants/apiConstants';

// ============================================================================
// EDI Field Mapping admin service — Spec mlinkone-buyer-po-field-mapping (group 8).
//
// Three POST endpoints, all returning the standard `Result<T>` envelope.
//   resolve      → effective per-property map (baseline + scope overrides),
//                  each property annotated by its winning source.
//   list-entries → raw override entries authored at the selected scope.
//   save         → create/supersede the override entries at the selected scope.
//
// Scope is identified by (tenantId, buyerPartyId?, supplierPartyId?). The BE
// builds the scope_path from these. Editable scopes are tenant / buyer /
// buyer+supplier only — supplier requires a buyer (enforced BE-side; reflected
// in the UI scope selector).
// ============================================================================

interface ResultEnvelope<T> {
  succeeded: boolean;
  statusCode?: number;
  messages?: string[];
  data?: T;
}

/** Scope tuple shared by resolve / list-entries / save requests. */
export interface EdiFieldMappingScope {
  tenantId: number;
  buyerPartyId?: number | null;
  supplierPartyId?: number | null;
}

/**
 * One row of the resolved effective map. `source` is the literal `"baseline"`
 * sentinel when the property comes from the code-defined standard map, else a
 * scope path (e.g. `/tenant:T/buyer:B`) of the override that won.
 * `scopeLabel` ("line" | "header" | "doc-then-header") is informational only.
 */
export interface ResolvedFieldMappingItem {
  targetProperty: string;
  ediCode: string;
  source: string;
  scopeLabel: string;
}

export interface ResolveFieldMappingResponse {
  items: ResolvedFieldMappingItem[];
}

/** One raw override entry (target property → EDI code) at a scope. */
export interface FieldMappingEntry {
  targetProperty: string;
  ediCode: string;
}

export interface ListFieldMappingEntriesResponse {
  scopePath: string;
  entries: FieldMappingEntry[];
}

export interface SaveFieldMappingRequest extends EdiFieldMappingScope {
  entries: FieldMappingEntry[];
}

export interface SaveFieldMappingResponse {
  id: number;
  scopePath: string;
}

/**
 * Thrown by `save` (and surfaced by the thunk) carrying the BE `Result.messages`
 * so the UI can render server-side validation errors verbatim.
 */
export class FieldMappingSaveError extends Error {
  constructor(public readonly messages: string[]) {
    super(messages.join(', ') || 'Failed to save field mapping');
    this.name = 'FieldMappingSaveError';
  }
}

function scopeBody(scope: EdiFieldMappingScope) {
  return {
    tenantId: scope.tenantId,
    buyerPartyId: scope.buyerPartyId ?? undefined,
    supplierPartyId: scope.supplierPartyId ?? undefined,
  };
}

export const ediFieldMappingService = {
  async resolve(scope: EdiFieldMappingScope): Promise<ResolveFieldMappingResponse> {
    try {
      const response = await apiClient.post<ResultEnvelope<ResolveFieldMappingResponse>>(
        API_EDI_FIELD_MAPPING_RESOLVE,
        scopeBody(scope),
      );
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Failed to resolve field mapping');
      }
      return response.data.data ?? { items: [] };
    } catch (error) {
      logger.error('Failed to resolve EDI field mapping', error);
      throw error;
    }
  },

  async listEntries(scope: EdiFieldMappingScope): Promise<ListFieldMappingEntriesResponse> {
    try {
      const response = await apiClient.post<ResultEnvelope<ListFieldMappingEntriesResponse>>(
        API_EDI_FIELD_MAPPING_LIST_ENTRIES,
        scopeBody(scope),
      );
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Failed to list field-mapping entries');
      }
      return response.data.data ?? { scopePath: '', entries: [] };
    } catch (error) {
      logger.error('Failed to list EDI field-mapping entries', error);
      throw error;
    }
  },

  async save(request: SaveFieldMappingRequest): Promise<SaveFieldMappingResponse> {
    try {
      const response = await apiClient.post<ResultEnvelope<SaveFieldMappingResponse>>(
        API_EDI_FIELD_MAPPING_SAVE,
        {
          ...scopeBody(request),
          entries: request.entries,
        },
      );
      if (!response.data.succeeded) {
        // Surface BE validation messages so the page can render them inline.
        throw new FieldMappingSaveError(response.data.messages ?? []);
      }
      return response.data.data ?? { id: 0, scopePath: '' };
    } catch (error) {
      if (error instanceof FieldMappingSaveError) throw error;
      // Network / unexpected error — wrap so the caller gets a message array too.
      throw new FieldMappingSaveError([getErrorMessage(error)]);
    }
  },
};
